import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Alert, Badge, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useSearchParams, useLocation } from 'react-router-dom';

import { RootState } from '../store';
import { DataTable } from '../components/common/DataTable/DataTable';
import { ColumnConfig } from '../components/common/DataTable/types';
import { FilterBar, FilterDefinition } from '../components/common/FilterControls';
import Select from '../components/common/Select';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { usePollInFlight } from '../hooks/usePollInFlight';
import { useAllUsers } from '../hooks/useAllUsers';
import UploadFilePreview from '../components/UploadFilePreview';
import AssignUploadModal from '../components/AssignUploadModal';
import ApplicantImportModal, { ApplicantImportModalHandle } from '../components/ApplicantImportModal';
import FileDropOverlay from '../components/FileDropOverlay';
import { usePageFileDrop } from '../hooks/usePageFileDrop';
import {
  getApplicantDocuments,
  getApplicantDocumentsId,
  deleteApplicantDocumentsId,
  useGetUsersId,
  usePostApplicantDocumentsRetry,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type {
  ApplicantDocument,
  ApplicantDocumentsSummary,
  GetApplicantDocuments200,
  GetApplicantDocumentsParams,
  User,
} from '../api/generated/model';

interface UserOption { value: string; label: string; }

const DOC_PATH = '/applicants/file-uploads';
const FETCH_LIMIT = 50;

const fmtDate = (s?: string) => (s ? new Date(s).toLocaleString() : '');
const fmtSize = (n?: number) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const statusVariant: Record<string, string> = { pending: 'info', needs_review: 'warning', resolved: 'success' };
const statusLabel: Record<string, string> = { pending: 'pending', needs_review: 'needs review', resolved: 'resolved' };

const filterDefs: FilterDefinition[] = [
  { type: 'text', field: 'search', label: 'Search', icon: 'bi-search', placeholder: 'File name or applicant…' },
  {
    type: 'select', field: 'source', label: 'Source', icon: 'bi-inbox',
    options: [
      { value: 'email', label: 'Email' },
      { value: 'manual_import', label: 'Manual' },
    ],
  },
  {
    type: 'select', field: 'status', label: 'Status', icon: 'bi-flag',
    options: [
      { value: 'pending', label: 'Pending' },
      { value: 'needs_review', label: 'Needs Review' },
      { value: 'resolved', label: 'Resolved' },
    ],
  },
];

const FileUploadLogTab: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const sortField = searchParams.get('sort_field') || 'applied_at';
  const sortDir: 'asc' | 'desc' = searchParams.get('sort_direction') === 'asc' ? 'asc' : 'desc';
  const search = searchParams.get('search') || '';
  const status = searchParams.get('status') || '';
  const source = searchParams.get('source') || '';
  const filters = { search, source, status };
  const [viewDoc, setViewDoc] = useState<ApplicantDocument | null>(null);
  const [assignDoc, setAssignDoc] = useState<ApplicantDocument | null>(null);
  const [showImport, setShowImport] = useState(false);
  const modalRef = useRef<ApplicantImportModalHandle>(null);
  const draggingFiles = usePageFileDrop((files) => {
    setShowImport(true);
    modalRef.current?.addFiles(files);
  }, location.pathname === DOC_PATH);

  const updateParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v) next.set(k, v);
            else next.delete(k);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (location.pathname !== DOC_PATH) return; // don't rewrite URL from another tab
    if (!searchParams.get('sort_field') || !searchParams.get('sort_direction')) {
      updateParams({ sort_field: sortField, sort_direction: sortDir });
    }
  }, [location.pathname, searchParams, updateParams, sortField, sortDir]);

  const isAdmin = user?.role === 'admin';
  const { data: usersData } = useAllUsers();
  const recruiterUserId = searchParams.get('recruiter_user_id') || '';
  const userOptions = useMemo<UserOption[]>(() => {
    const opts: UserOption[] = [{ value: '', label: 'All recruiters' }];
    (usersData || [])
      .filter((u: User) => u.active !== false && u.id && u.id !== user?.id)
      .sort((a: User, b: User) => `${a.first_name ?? ''} ${a.last_name ?? ''}`.localeCompare(`${b.first_name ?? ''} ${b.last_name ?? ''}`))
      .forEach((u: User) => opts.push({ value: u.id!, label: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email || u.id! }));
    return opts;
  }, [usersData, user?.id]);
  const selectedUserOption = userOptions.find((o) => o.value === recruiterUserId) || userOptions[0];
  const [showFilterBar, setShowFilterBar] = useState(false);
  const activeFilterCount = [search, source, status].filter(Boolean).length;

  const buildQueryParams = useCallback(
    (cursor: string | undefined) => {
      if (cursor) return { cursor, limit: FETCH_LIMIT };
      return {
        ...(search ? { search } : {}),
        ...(source ? { source } : {}),
        ...(status ? { status } : {}),
        ...(recruiterUserId ? { recruiter_user_id: recruiterUserId } : {}),
        sort_field: sortField,
        sort_direction: sortDir,
        limit: FETCH_LIMIT,
      };
    },
    [search, source, status, recruiterUserId, sortField, sortDir]
  );

  const { allItems, totalCount, isLoading, hasMore, handleLoadMore, reset, updateItem, setAllItems } = usePaginatedQuery<ApplicantDocument, GetApplicantDocuments200, ApplicantDocumentsSummary>({
    queryKey: 'documents-log',
    buildQueryParams,
    queryFn: (params) => getApplicantDocuments(params as GetApplicantDocumentsParams),
    getRowKey: (d) => d.id,
  });

  // Extraction runs async on the harvest worker, so a freshly-created document
  // lands as 'pending' and later flips to resolved/needs_review. Poll just the
  // loaded in-flight rows and patch each in place (no reset — cursor/sort/scroll stay put).
  usePollInFlight<ApplicantDocument>({
    items: allItems,
    isInFlight: (d) => d.status === 'pending',
    getKey: (d) => d.id,
    fetchOne: (id) => getApplicantDocumentsId(id),
    updateItem,
    enabled: location.pathname === DOC_PATH,
  });

  const retryMutation = usePostApplicantDocumentsRetry();
  const [retryMsg, setRetryMsg] = useState<{ variant: string; text: string } | null>(null);
  const handleRetry = async () => {
    setRetryMsg(null);
    try {
      const res = await retryMutation.mutateAsync({ params: recruiterUserId ? { recruiter_user_id: recruiterUserId } : undefined });
      const n = res?.retried ?? 0;
      reset();
      setRetryMsg(
        n > 0
          ? { variant: 'success', text: `Re-queued ${n} failed document${n === 1 ? '' : 's'} for re-extraction.` }
          : { variant: 'secondary', text: 'No failed documents to retry.' }
      );
    } catch (e: any) {
      setRetryMsg({ variant: 'danger', text: e?.response?.data?.message || e?.response?.data?.error || 'Retry failed.' });
    }
  };

  const handleDelete = async (d: ApplicantDocument) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Delete “${d.file_name}”? This removes the document and its file.`)) return;
    setRetryMsg(null);
    try {
      await deleteApplicantDocumentsId(d.id);
      setAllItems((prev) => prev.filter((x) => x.id !== d.id));
    } catch (e: any) {
      setRetryMsg({ variant: 'danger', text: e?.response?.data?.message || e?.response?.data?.error || 'Delete failed.' });
    }
  };

  const columns: ColumnConfig<ApplicantDocument>[] = [
    { key: 'file_name', label: 'File', sortable: true, width: 260, renderCell: (d) => (
      <a href="#" className="text-truncate d-block" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setViewDoc(d); }}>{d.file_name}</a>
    ) },
    { key: 'type', label: 'Type', sortable: true, width: 110, renderCell: (d) => (d.type === 'cover_letter' ? 'cover' : 'resume') },
    { key: 'source', label: 'Source', sortable: true, width: 100, renderCell: (d) => (d.source === 'manual_import' ? 'manual' : 'email') },
    { key: 'size', label: 'Size', sortable: true, width: 90, renderCell: (d) => fmtSize(d.size) },
    { key: 'applicant', label: 'Applicant', sortable: false, width: 220, renderCell: (d) => d.extraction?.email || '' },
    { key: 'resolution_reason', label: 'Note', sortable: false, width: 300, renderCell: (d) => <span className="text-wrap" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{d.resolution_reason || ''}</span> },
    { key: 'applied_at', label: 'Received', sortable: true, width: 160, renderCell: (d) => fmtDate(d.applied_at) },
    { key: 'status', label: 'Status', sortable: false, width: 130, renderCell: (d) => <Badge bg={statusVariant[d.status || ''] || 'secondary'} text={statusVariant[d.status || ''] === 'warning' ? 'dark' : undefined}>{statusLabel[d.status || ''] || d.status}</Badge> },
    { key: 'actions', label: '', sortable: false, width: 130, renderCell: (d) => (
      <div className="d-flex gap-1 justify-content-end text-nowrap">
        {d.status === 'needs_review' && (
          <Button size="sm" variant="outline-primary" onClick={(e) => { e.stopPropagation(); setAssignDoc(d); }}>Assign</Button>
        )}
        <Button size="sm" variant="outline-danger" title="Delete" onClick={(e) => { e.stopPropagation(); handleDelete(d); }}><i className="bi bi-trash" /></Button>
      </div>
    ) },
  ];

  return (
    <>
      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px', overflow: 'hidden' }}>
        <Card.Header className="bg-primary text-white flex-shrink-0">
          <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
                <span className="text-primary fw-semibold">
                  <i className="bi bi-file-earmark-text me-1"></i>
                  Documents
                </span>
                {isAdmin && (
                  <div style={{ minWidth: 220 }}>
                    <Select<UserOption>
                      options={userOptions}
                      value={selectedUserOption}
                      onChange={(opt) => updateParams({ recruiter_user_id: opt?.value || undefined })}
                      isClearable={recruiterUserId !== ''}
                    />
                  </div>
                )}
                <div className="border-end border-primary mx-2" style={{ height: '24px' }}></div>
                <Button
                  variant={activeFilterCount > 0 ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setShowFilterBar(!showFilterBar)}
                >
                  <i className="bi bi-funnel me-1"></i>
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="badge bg-light text-primary ms-1">{activeFilterCount}</span>
                  )}
                </Button>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
                <Button size="sm" variant="outline-primary" onClick={() => setShowImport(true)}>
                  <i className="bi bi-upload me-1" />Upload files
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleRetry}
                  disabled={retryMutation.isPending}
                  title="Re-queue failed documents for re-extraction"
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  {retryMutation.isPending ? 'Retrying…' : 'Retry failed'}
                </Button>
              </div>
              <div className="rounded px-3 py-1">
                <span className="text-white">
                  {allItems.length > 0 ? (
                    <>{totalCount ?? allItems.length} total</>
                  ) : (
                    'No documents'
                  )}
                </span>
              </div>
            </div>
          </div>
          {showFilterBar && (
            <FilterBar
              filters={filters}
              filterDefs={filterDefs}
              onFilterChange={(f, v) => updateParams({ [f]: v || undefined })}
              onClearFilters={() => updateParams({ search: undefined, source: undefined, status: undefined })}
            />
          )}
        </Card.Header>
        {retryMsg && (
          <Alert
            variant={retryMsg.variant}
            className="m-2 py-2 d-flex justify-content-between align-items-center"
          >
            <span>{retryMsg.text}</span>
            <button type="button" className="btn-close" aria-label="Close" onClick={() => setRetryMsg(null)} />
          </Alert>
        )}
        <Card.Body className="flex-grow-1 overflow-hidden p-0" style={{ minHeight: 0 }}>
          <DataTable<ApplicantDocument>
            data={allItems}
            columns={columns}
            getRowKey={(d) => d.id}
            sortConfig={{ field: sortField, direction: sortDir }}
            onSortChange={(field, dir) => updateParams({ sort_field: field, sort_direction: dir })}
            pagination={{ hasMore, limit: FETCH_LIMIT }}
            onLoadMore={handleLoadMore}
            isLoading={isLoading}
            preferencesKey="documents-log"
            userId={user?.id}
            userPreferences={userData?.preferences}
            maxHeight="100%"
            enableStickyHeader
            enableColumnReorder
            enableColumnResize
            enableColumnVisibility
            emptyState={<Alert variant="secondary" className="m-3">No documents yet.</Alert>}
          />
        </Card.Body>
      </Card>
      <UploadFilePreview doc={viewDoc} onClose={() => setViewDoc(null)} />
      <AssignUploadModal doc={assignDoc} onClose={() => setAssignDoc(null)} onAssigned={reset} />
      <FileDropOverlay show={draggingFiles} />
      <ApplicantImportModal ref={modalRef} show={showImport} onClose={() => setShowImport(false)} onUploaded={reset} />
    </>
  );
};

export default FileUploadLogTab;
